import { useCallback, useEffect, useState } from "react";
import type { Key } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { mutationResponse } from "@/utils/trpc";
import type { OrgDetail } from "../../types";
import { useAdminGridOptions } from "../../utils/gridState";
import { AdminGrid } from "../AdminGrid";
import { TabLayout } from "../TabLayout";

export function ClientPanelsTable({ org }: { org: OrgDetail }) {
  const panels = org.panels;

  const updateOrder = trpc.admin.org.updatePanelOrder.useMutation();
  const updatePrice = trpc.admin.org.updatePanelPrice.useMutation();

  const { options } = useAdminGridOptions(
    panels,
    [
      {
        field: "panel.name",
        rowDrag: true,
        cellRenderer: (params) => (
          <Link
            to="/admin/panels/$id/edit"
            params={{ id: params.data.panelId }}
          >
            {params.data.panel.name}
          </Link>
        ),
        cellRendererParams: (params) => ({
          id: params.data.panelId,
          name: params.data.panel.name,
        }),
      },
      {
        field: "panel.enabled",
        headerName: "Enabled",
      },
      {
        colId: "price",
        headerName: "Price",
        editable: true,
        type: "numericColumn",

        cellRendererParams: (params) => ({
          panelPrice: params.data.panel.price ?? 0,
          price: params.data.price,
        }),

        cellRenderer: (params) => {
          return (
            <>
              {params.data.price !== null ? (
                <div className="flex items-center gap-1 justify-end w-full">
                  <Text className="line-through" muted>
                    ${params.panelPrice.toFixed(2)}
                  </Text>
                  <Text>${params.price.toFixed(2)}</Text>
                </div>
              ) : (
                <Text>${params.panelPrice.toFixed(2)}</Text>
              )}
            </>
          );
        },
        valueGetter: (params) => {
          return params.data?.price ?? params.data?.panel.price ?? 0;
        },
      },
    ],
    {
      sideBar: undefined,
      domLayout: "autoHeight",
      readOnlyEdit: true,
      getRowId: (row) => row.data.panelId.toString(),
      onCellEditRequest: (e) => {
        if (e.column.getColId() !== "price") {
          return;
        }

        const newPrice = e.newValue === "" ? null : Number(e.newValue);

        // Validate that the price is not NaN (catches invalid user input like non-numeric strings)
        if (newPrice !== null && Number.isNaN(newPrice)) {
          toast.error(
            "Invalid price: please enter a valid number or leave blank",
          );
          return;
        }

        e.api.showLoadingOverlay();

        updatePrice.mutate(
          {
            id: org.id,
            panelId: e.data.panelId,
            price: newPrice,
          },
          mutationResponse({
            errorTitle: "Error updating price",
            successMessage: "Price updated for client",
            onSuccess: () => e.api.hideOverlay(),
            onError: () => e.api.hideOverlay(),
          }),
        );
      },
      onRowDragEnd: (e) => {
        const ids: number[] = [];
        e.api.forEachNode((node) => {
          if (node.data?.panelId) {
            ids.push(node.data.panelId);
          }
        });
        updateOrder.mutate(
          { id: org.id, panels: ids },
          mutationResponse({
            errorTitle: "Error reordering panels",
            successMessage: "Panels reordered",
          }),
        );
      },
    },
  );

  return (
    <TabLayout
      description="This client will be able to select the following test panels."
      actions={<UpdatePanelsDialog org={org} />}
    >
      <AdminGrid {...options} />
    </TabLayout>
  );
}

function UpdatePanelsDialog({ org }: { org: OrgDetail }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<Key[]>(org.panels.map((p) => p.panelId));
  const { data, isLoading } = trpc.panels.all.useQuery();

  const updatePanels = trpc.admin.org.updatePanels.useMutation();

  useEffect(() => {
    if (open) {
      setKeys(org.panels.map((p) => p.panelId));
    }
  }, [open, org.panels]);

  const onSelectionChange = useCallback(
    (keys: "all" | Set<Key>) => {
      if (keys === "all") {
        setKeys(data?.map((t) => t.id) || []);
      } else {
        setKeys(Array.from(keys));
      }
    },
    [data],
  );

  const onUpdate = useCallback(() => {
    updatePanels.mutate(
      { id: org.id, panels: keys.map((k) => Number(k)) },
      mutationResponse({
        errorTitle: "Failed to update panels",
        successMessage: "Panels updated",
        onSuccess: () => setOpen(false),
      }),
    );
  }, [keys, org.id, updatePanels]);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button variant="secondary" onPress={() => setOpen(true)}>
        Edit panels
      </Button>
      <Modal isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Update panels</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <GridList
              items={data || []}
              selectionMode="multiple"
              selectedKeys={keys}
              onSelectionChange={onSelectionChange}
              // isLoading={isLoading}
            >
              {(test) => (
                <GridListItem key={test.id} textValue={test.name}>
                  <Text>{test.name}</Text>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button isPending={updatePanels.isPending} onPress={onUpdate}>
              Update
            </Button>

            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
